import { INTERNAL_MODEL_TYPE } from '@/delivery-type-plugin/capabilities/model-subscription-provider';
import i18n from '@/i18n';

import { MS_PROVIDER_TOKEN_UNIT } from '../../../capabilities/model-subscription-provider/constants';
import { buildMsTokenPricingResource } from '../../../capabilities/model-subscription-provider/pricingResource';
import { deriveMsUseSubResourcePricing } from '../../../capabilities/model-subscription-provider/useSubResourcePricingField';
import { normalizeQuantityUnit } from '../../../components/quantityUnit';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { previewRow } from '../../../platform/display-descriptors/previewRow';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import { formatQuantityUnit } from '../../../utils/formatQuantityUnit';
import { filterMsSpecByEnabledModules, isMsModuleEnabled } from './enabledModules';
import { createMsDefaultLimits } from './payload';
import type { MsApiPayload, MsSpec } from './schema';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function formatMsStrategyProviderDisplay(provider: string | undefined, translate: TranslateFn) {
  const value = String(provider || '').trim();
  if (!value) return '';
  if (value === INTERNAL_MODEL_TYPE) {
    return translate('data-center:model.provider.builtin');
  }
  return value;
}

/** 是否处于计价展示上下文（有厂商设备元数据）。预览里也会注入 ctx.sku 做模块过滤，不能单靠 ctx.sku 判断。 */
function hasMsPricingVendorMeta(spec: MsSpec) {
  const pricing = spec.providerPricing;
  if (!pricing) return false;
  return Boolean(pricing.vendor || pricing.device || pricing.vendorID || pricing.deviceID);
}

/** 供应商：与输入框一致——计价用 vendor 名称；直建用 strategyProvider；禁止回退 ID */
function resolveMsSupplierDisplay(spec: MsSpec, translate: TranslateFn) {
  const fromPricing = String(spec.providerPricing?.vendor || '').trim();
  if (fromPricing) return fromPricing;
  if (hasMsPricingVendorMeta(spec)) {
    // 计价态 strategyDisplayName 存供应商展示名
    return String(spec.strategyDisplayName || '').trim();
  }
  return formatMsStrategyProviderDisplay(spec.strategyProvider, translate);
}

/** 模型：与输入框一致——计价用 device 名称；直建用上游模型 strategyDisplayName；禁止回退 strategyName（deviceID / 资源名） */
function resolveMsModelDisplay(spec: MsSpec) {
  const fromPricing = String(spec.providerPricing?.device || '').trim();
  if (fromPricing) return fromPricing;
  if (hasMsPricingVendorMeta(spec)) {
    return '';
  }
  return String(spec.strategyDisplayName || '').trim();
}

function formatStringList(value: unknown) {
  if (!Array.isArray(value)) return '-';
  const items = value.map((item) => String(item || '').trim()).filter(Boolean);
  return items.length ? items.join('、') : '-';
}

function formatLimit(enabled: boolean | undefined, value: unknown, disabledLabel: string) {
  if (!enabled) return disabledLabel;
  if (value == null || value === '') return '-';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  return numericValue.toLocaleString();
}

/** 计价模式：配额数值后附加单位（如 100 百万token）；直建不带单位 */
function formatTokenQuotaLimit(
  enabled: boolean | undefined,
  value: unknown,
  disabledLabel: string,
  showUnit: boolean,
  lang?: string,
  quantityUnit?: string,
) {
  const formatted = formatLimit(enabled, value, disabledLabel);
  if (!showUnit || !enabled || formatted === disabledLabel || formatted === '-') return formatted;
  const unitLabel = formatQuantityUnit(quantityUnit || MS_PROVIDER_TOKEN_UNIT, lang);
  return unitLabel ? `${formatted} ${unitLabel}` : formatted;
}

function formatWindowLimit(enabled: boolean | undefined, value: unknown, windowSeconds: unknown, disabledLabel: string) {
  if (!enabled) return disabledLabel;
  if (windowSeconds == null || windowSeconds === '' || value == null || value === '') return '-';
  return `${formatLimit(true, value, disabledLabel)} / ${formatLimit(true, windowSeconds, disabledLabel)}s`;
}

function hasEnabledQuota(limit?: { enabled?: boolean; value?: number }) {
  return Boolean(limit?.enabled && Number(limit.value) > 0);
}

function hasEnabledWindowQuota(limit?: { enabled?: boolean; value?: number; windowSeconds?: number }) {
  return Boolean(limit?.enabled && Number(limit.value) > 0 && Number(limit.windowSeconds) > 0);
}

export function serializeMsApiBody(spec: MsSpec, ctx: PipelineContext): MsApiPayload {
  const alias = spec.alias || String(ctx.overlay?.alias || ctx.overlay?.name || '');
  const workspace = spec.workspace || String(ctx.overlay?.workspace || '');
  const creator = spec.creator || String(ctx.overlay?.creator || '');
  const strategyName = String(spec.llmgwStrategyRef?.external?.strategyName || spec.strategyName || '');
  const defaults = createMsDefaultLimits();

  return {
    alias: String(alias),
    workspace: String(workspace),
    allowedClientCIDRs: spec.allowedClientCIDRs,
    allowedClientIPs: spec.allowedClientIPs,
    creator: String(creator),
    description: spec.description ?? '',
    llmgwStrategyRef: {
      external: {
        strategyName,
      },
    },
    rateLimits: spec.rateLimits ?? defaults.rateLimits,
    resourceLimits: spec.resourceLimits ?? defaults.resourceLimits,
  };
}

export function pickMsPresetFields(spec: MsSpec, ctx?: PipelineContext): unknown {
  const enabledModules = ctx?.sku?.enabledModules;
  const skuType = ctx?.sku?.skuType;
  const filtered = filterMsSpecByEnabledModules(spec, enabledModules, skuType);
  const formValues = {
    alias: filtered.alias,
    workspace: filtered.workspace,
    description: filtered.description,
    strategyName: filtered.strategyName,
    strategyDisplayName: filtered.strategyDisplayName,
    strategyProvider: filtered.strategyProvider,
    ...(filtered.providerPricing ? { providerPricing: filtered.providerPricing } : {}),
    allowedClientCIDRs: filtered.allowedClientCIDRs,
    allowedClientIPs: filtered.allowedClientIPs,
    inputTokens: filtered.resourceLimits?.inputTokens?.enabled ? filtered.resourceLimits.inputTokens.value : undefined,
    outputTokens: filtered.resourceLimits?.outputTokens?.enabled ? filtered.resourceLimits.outputTokens.value : undefined,
    billingTotalTokens: filtered.resourceLimits?.billingTotalTokens?.enabled ? filtered.resourceLimits.billingTotalTokens.value : undefined,
    useSubResourcePricing: deriveMsUseSubResourcePricing({
      useSubResourcePricing: filtered.useSubResourcePricing,
      billingTotalTokens: filtered.resourceLimits?.billingTotalTokens?.enabled
        ? filtered.resourceLimits.billingTotalTokens.value
        : undefined,
      inputTokens: filtered.resourceLimits?.inputTokens?.enabled ? filtered.resourceLimits.inputTokens.value : undefined,
      outputTokens: filtered.resourceLimits?.outputTokens?.enabled ? filtered.resourceLimits.outputTokens.value : undefined,
    }),
    requests: filtered.resourceLimits?.requests?.enabled ? filtered.resourceLimits.requests.value : undefined,
    inputTokensValue: filtered.rateLimits?.inputTokens?.enabled ? filtered.rateLimits.inputTokens.value : undefined,
    inputTokensWindowSeconds: filtered.rateLimits?.inputTokens?.enabled ? filtered.rateLimits.inputTokens.windowSeconds : undefined,
    outputTokensValue: filtered.rateLimits?.outputTokens?.enabled ? filtered.rateLimits.outputTokens.value : undefined,
    outputTokensWindowSeconds: filtered.rateLimits?.outputTokens?.enabled ? filtered.rateLimits.outputTokens.windowSeconds : undefined,
    switches: filtered.switches,
    ...(enabledModules ? { skuResourceModules: enabledModules } : {}),
  };
  return formValues;
}

export function buildMsDisplayDescriptors(
  spec: MsSpec,
  ctx?: PipelineContext,
  translate: TranslateFn = i18n.t.bind(i18n),
): DisplayDescriptor[] {
  const enabledModules = ctx?.sku?.enabledModules;
  const skuType = ctx?.sku?.skuType;
  const filtered = filterMsSpecByEnabledModules(spec, enabledModules, skuType);
  const rows: DisplayDescriptor[] = [];
  const t = translate;
  const disabledLabel = t('data-center:model.subscription.limit.disabled');

  if (isMsModuleEnabled('baseInfo', enabledModules, skuType)) {
    rows.push(
      previewRow('alias', 'baseInfo', 'common:name', filtered.alias, t),
      previewRow('workspace', 'baseInfo', 'common:workspace', filtered.workspace, t),
      previewRow('description', 'baseInfo', 'common:description', filtered.description, t),
    );
  }

  if (isMsModuleEnabled('modelProvider', enabledModules, skuType)) {
    const billingTotalQuota = filtered.resourceLimits?.billingTotalTokens;
    const inputQuota = filtered.resourceLimits?.inputTokens;
    const outputQuota = filtered.resourceLimits?.outputTokens;
    const supplier = resolveMsSupplierDisplay(filtered, t);
    const model = resolveMsModelDisplay(filtered);
    // 计价/下单 ctx 带 paymentTiming；直建预览不带，配额不展示单位
    const showTokenUnit = Boolean(ctx?.paymentTiming);
    const lang = i18n.language;
    const tokenQuantityUnit = normalizeQuantityUnit(filtered.providerPricing?.quantityUnit);
    // 商品卡片 / 预览：无供应商/模型或未配置配额时不展示对应行
    if (supplier) {
      rows.push(previewRow('vendor', 'modelProvider', 'data-center:model.subscription.supplier', supplier, t));
    }
    if (model) {
      rows.push(previewRow('model', 'modelProvider', 'data-center:model.subscription.model', model, t));
    }
    if (hasEnabledQuota(billingTotalQuota)) {
      rows.push(
        previewRow(
          'token_total',
          'modelProvider',
          'data-center:model.subscription.token.total.label',
          formatTokenQuotaLimit(true, billingTotalQuota!.value, disabledLabel, showTokenUnit, lang, tokenQuantityUnit),
          t,
        ),
      );
    }
    if (hasEnabledQuota(inputQuota)) {
      rows.push(
        previewRow(
          'token_input',
          'modelProvider',
          'data-center:model.subscription.token.input',
          formatTokenQuotaLimit(true, inputQuota!.value, disabledLabel, showTokenUnit, lang, tokenQuantityUnit),
          t,
        ),
      );
    }
    if (hasEnabledQuota(outputQuota)) {
      rows.push(
        previewRow(
          'token_output',
          'modelProvider',
          'data-center:model.subscription.token.output',
          formatTokenQuotaLimit(true, outputQuota!.value, disabledLabel, showTokenUnit, lang, tokenQuantityUnit),
          t,
        ),
      );
    }
  }

  if (isMsModuleEnabled('other', enabledModules, skuType)) {
    rows.push(
      previewRow(
        'allowed_cidrs',
        'other',
        'data-center:model.subscription.allowed.client.cidrs',
        formatStringList(filtered.allowedClientCIDRs),
        t,
      ),
      previewRow(
        'allowed_ips',
        'other',
        'data-center:model.subscription.allowed.client.ips',
        formatStringList(filtered.allowedClientIPs),
        t,
      ),
    );
    if (hasEnabledQuota(filtered.resourceLimits?.requests)) {
      rows.push(
        previewRow(
          'requests_total',
          'other',
          'data-center:model.subscription.requests.total',
          formatLimit(true, filtered.resourceLimits!.requests!.value, disabledLabel),
          t,
        ),
      );
    }
    if (hasEnabledWindowQuota(filtered.rateLimits?.inputTokens)) {
      rows.push(
        previewRow(
          'input_rate',
          'other',
          'data-center:model.subscription.input.limit',
          formatWindowLimit(true, filtered.rateLimits!.inputTokens!.value, filtered.rateLimits!.inputTokens!.windowSeconds, disabledLabel),
          t,
        ),
      );
    }
    if (hasEnabledWindowQuota(filtered.rateLimits?.outputTokens)) {
      rows.push(
        previewRow(
          'output_rate',
          'other',
          'data-center:model.subscription.output.limit',
          formatWindowLimit(
            true,
            filtered.rateLimits!.outputTokens!.value,
            filtered.rateLimits!.outputTokens!.windowSeconds,
            disabledLabel,
          ),
          t,
        ),
      );
    }
  }

  return rows;
}

export function buildMsResourceConfigs(spec: MsSpec, ctx?: PipelineContext): ResourceType[] {
  const enabledModules = ctx?.sku?.enabledModules;
  const skuType = ctx?.sku?.skuType;
  // 计价资源依赖供应商选型 + Token 配额
  if (!isMsModuleEnabled('modelProvider', enabledModules, skuType)) {
    return [];
  }

  const filtered = filterMsSpecByEnabledModules(spec, enabledModules, skuType);
  const resource = buildMsTokenPricingResource({
    useSubResourcePricing: filtered.useSubResourcePricing,
    billingTotalTokens: filtered.resourceLimits?.billingTotalTokens,
    inputTokens: filtered.resourceLimits?.inputTokens,
    outputTokens: filtered.resourceLimits?.outputTokens,
    strategyName: filtered.strategyName,
    strategyDisplayName: filtered.strategyDisplayName,
    providerPricing: filtered.providerPricing,
    paymentTiming: ctx?.paymentTiming,
    skuType,
    purpose: ctx?.resourceQuantityPurpose,
  });

  return resource ? [resource] : [];
}
